import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import StudyResourceService, { StudyResource } from "../../_services/StudyResourceService";
import { STUDY_RESOURCE_TYPE } from "../../_constants/studyResourceType";
import DecisionResourceStudio, { STUDIO_PORTLETS, resolvePortletId } from "./components/DecisionResourceStudio";
import { isExecutableWorkflow, validateWorkflow, WorkflowV2 } from "./components/workflow-types";
import { ClinicalSourceRecord } from "./components/clinical-spec-types";
import ClinicalMastersService, { ClinicalMasters } from "../../_services/ClinicalMastersService";
import "./StudyResourcesPage.css";

/**
 * Decision Resource Studio — the standalone governance surface (DeciiQ:
 * "Configure. Govern. Publish."). The landing view presents decision
 * resources (Interactive Pathway, type 40) as governed artifacts with their
 * safety profile; opening one loads the full-page portlet studio.
 *
 * The generic Study Resources table remains the operational CRUD list; this
 * page is the SME-facing studio over the same rows — same API, no new tables.
 */

interface GovernanceProfile {
  executable: boolean;
  valid: boolean;
  issueCount: number;
  pathways: number;
  states: number;
  hardStops: number;
  checkpoints: number;
  safetyExits: number;
  title: string;
}

const profileOf = (content?: string | null): GovernanceProfile => {
  const empty: GovernanceProfile = {
    executable: false, valid: true, issueCount: 0, pathways: 0,
    states: 0, hardStops: 0, checkpoints: 0, safetyExits: 0, title: "",
  };
  try {
    const cfg = JSON.parse(content || "{}");
    const base = { ...empty, pathways: (cfg.pathways || []).length, title: cfg.title || "" };
    const wf = cfg.workflow;
    if (!isExecutableWorkflow(wf)) {
      return { ...base, states: Array.isArray(wf) ? wf.length : 0 };
    }
    const def = wf as WorkflowV2;
    const issues = validateWorkflow(def);
    return {
      ...base,
      executable: true,
      valid: issues.length === 0,
      issueCount: issues.length,
      states: def.states.length,
      hardStops: def.transitions.filter((t) => t.kind === "advance" && t.guard?.type === "field").length,
      checkpoints: def.transitions.filter((t) => t.guard?.type === "aiReview").length,
      safetyExits: def.transitions.filter((t) => t.kind !== "advance").length,
    };
  } catch {
    return { ...empty, valid: false };
  }
};

const isBlankConfig = (content?: string | null): boolean => {
  try {
    const cfg = JSON.parse(content || "{}");
    return !cfg.title && !(cfg.pathways || []).length && !cfg.workflow;
  } catch {
    return true;
  }
};

/** Base-template / lineage / domain markers carried inside the resource config. */
interface TemplateMeta {
  isBase: boolean;
  isDomainMaster: boolean;
  condition?: string;
  templateKey?: string;
  templateVersion?: number;
  basedOn?: { templateKey?: string | null; version?: number | null; resourceId?: number; copiedAt?: string };
}

const templateMetaOf = (content?: string | null): TemplateMeta => {
  try {
    const cfg = JSON.parse(content || "{}");
    return {
      isBase: !!cfg.isBaseTemplate,
      isDomainMaster: !!cfg.isDomainMaster,
      condition: cfg.condition,
      templateKey: cfg.templateKey,
      templateVersion: cfg.templateVersion,
      basedOn: cfg.basedOn,
    };
  } catch {
    return { isBase: false, isDomainMaster: false };
  }
};

const DecisionResourceStudioPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();

  const [resources, setResources] = useState<StudyResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [selected, setSelected] = useState<StudyResource | null>(null);
  const [opening, setOpening] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [contentDraft, setContentDraft] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const [creating, setCreating] = useState(false);

  // ?portlet=guardrails etc. — launcher artifact cards land on that studio portlet
  const targetPortlet = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("portlet");
    const id = resolvePortletId(raw);
    return STUDIO_PORTLETS.find((p) => p.id === id) || null;
  }, [location.search]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const res = await StudyResourceService.getStudyResources({
        typeId: STUDY_RESOURCE_TYPE.INTERACTIVE_PATHWAY,
        sortBy: "lastModifiedDate",
        sortOrder: "desc",
        limit: 100,
      });
      setResources(res.data || []);
    } catch (err: any) {
      setListError(err?.response?.data?.error?.message || err?.message || "Failed to load decision resources");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const openResource = useCallback(async (id: number) => {
    setOpening(true);
    setSaveError(null);
    try {
      const res = await StudyResourceService.getStudyResourceById(id);
      const resource = res.data;
      if (!resource) throw new Error("Resource not found");
      setSelected(resource);
      setNameDraft(resource.name || "");
      setContentDraft(resource.content || "{}");
      setDirty(false);
      setJustSaved(false);
      const params = new URLSearchParams(location.search);
      if (params.get("id") !== String(id)) {
        const portletParam = params.get("portlet");
        history.replace(`/decision-resource-studio?id=${id}${portletParam ? `&portlet=${portletParam}` : ""}`);
      }
    } catch (err: any) {
      setListError(err?.response?.data?.error?.message || err?.message || `Failed to open resource ${id}`);
    } finally {
      setOpening(false);
    }
  }, [history, location.search]);

  // Deep link: /decision-resource-studio?id=2925
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idParam = params.get("id");
    if (idParam && !isNaN(parseInt(idParam, 10)) && !selected && !opening) {
      openResource(parseInt(idParam, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const backToList = () => {
    if (dirty && !window.confirm("Discard unsaved changes to this decision resource?")) return;
    setSelected(null);
    setDirty(false);
    setSaveError(null);
    history.replace("/decision-resource-studio");
    loadList();
  };

  const save = async () => {
    if (!selected?.id) return;
    setSaving(true);
    setSaveError(null);
    try {
      await StudyResourceService.updateStudyResource(selected.id, {
        name: nameDraft,
        content: contentDraft,
      });
      setDirty(false);
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 2500);
    } catch (err: any) {
      const details = err?.response?.data?.error?.details;
      const detailText = Array.isArray(details) ? ` — ${details.map((d: any) => d.message || d).join("; ")}` : "";
      setSaveError((err?.response?.data?.error?.message || err?.message || "Save failed") + detailText);
    } finally {
      setSaving(false);
    }
  };

  const createNew = async () => {
    setCreating(true);
    setListError(null);
    try {
      const res = await StudyResourceService.createStudyResource({
        name: "Untitled Decision Resource",
        typeId: STUDY_RESOURCE_TYPE.INTERACTIVE_PATHWAY,
        content: "{}",
      });
      const newId = res.id ?? res.data?.id;
      if (!newId) throw new Error("Create succeeded but no id returned");
      await openResource(newId);
    } catch (err: any) {
      setListError(err?.response?.data?.error?.message || err?.message || "Failed to create decision resource");
    } finally {
      setCreating(false);
    }
  };

  /**
   * Start from a SynerGen reference: plain copy of the template content (no runtime
   * link), minus the template flags, stamped with `basedOn` provenance so the
   * lineage is never lost.
   */
  const createFromBase = async (template: StudyResource) => {
    setCreating(true);
    setListError(null);
    try {
      const cfg = JSON.parse(template.content || "{}");
      const { isBaseTemplate, templateKey, templateVersion, ...rest } = cfg;
      rest.basedOn = {
        templateKey: templateKey ?? null,
        version: templateVersion ?? null,
        resourceId: template.id,
        copiedAt: new Date().toISOString(),
      };
      const res = await StudyResourceService.createStudyResource({
        name: `${template.name} — Copy`,
        typeId: STUDY_RESOURCE_TYPE.INTERACTIVE_PATHWAY,
        content: JSON.stringify(rest, null, 2),
      });
      const newId = res.id ?? res.data?.id;
      if (!newId) throw new Error("Create succeeded but no id returned");
      await openResource(newId);
    } catch (err: any) {
      setListError(err?.response?.data?.error?.message || err?.message || "Failed to copy the base pathway");
    } finally {
      setCreating(false);
    }
  };

  // ?condition=hypertension — the Clinical Domains catalog filters the studio per condition
  const conditionFilter = useMemo(
    () => new URLSearchParams(location.search).get("condition"),
    [location.search],
  );
  const inScope = useCallback(
    (r: StudyResource) => {
      const m = templateMetaOf(r.content);
      return !m.isDomainMaster && (!conditionFilter || m.condition === conditionFilter);
    },
    [conditionFilter],
  );
  const baseTemplates = useMemo(
    () => resources.filter((r) => templateMetaOf(r.content).isBase && inScope(r)),
    [resources, inScope],
  );
  const clientResources = useMemo(
    () => resources.filter((r) => !templateMetaOf(r.content).isBase && inScope(r)),
    [resources, inScope],
  );

  const draftProfile = useMemo(
    () => (selected ? profileOf(contentDraft) : null),
    [selected, contentDraft],
  );
  const draftMeta = useMemo(
    () => (selected ? templateMetaOf(contentDraft) : null),
    [selected, contentDraft],
  );

  // Clinical masters (from the clinical-masters API) — feed every vocabulary
  // picker in the studio (sources §5/§13.3, parameters, medication classes, roles).
  const [clinicalMasters, setClinicalMasters] = useState<ClinicalMasters | null>(null);
  useEffect(() => {
    let cancelled = false;
    ClinicalMastersService.getClinicalMasters()
      .then((m) => { if (!cancelled) setClinicalMasters(m); })
      .catch(() => { /* masters unavailable — pickers simply don't render */ });
    return () => { cancelled = true; };
  }, []);
  const sourceRegistry: ClinicalSourceRecord[] = useMemo(
    () => (clinicalMasters?.sources || []).map((s) => ({
      id: s.sourceKey,
      name: s.name,
      publisher: s.publisher ?? undefined,
      year: s.year ?? undefined,
      type: s.type as ClinicalSourceRecord["type"],
      approvalStatus: s.approvalStatus as ClinicalSourceRecord["approvalStatus"],
      ragStatus: (s.ragStatusManual ?? "pending") as ClinicalSourceRecord["ragStatus"],
      resourceIds: s.resourceIds,
    })),
    [clinicalMasters],
  );

  // ── Studio view (a resource is open) ──
  if (selected) {
    return (
      <div className="drs-page">
        <div className="drs-page-toolbar">
          <Button type="button" size="sm" variant="secondary" onClick={backToList}>
            ← All decision resources
          </Button>
          <Form.Control
            className="drs-page-name"
            value={nameDraft}
            onChange={(e) => { setNameDraft(e.target.value); setDirty(true); }}
            placeholder="Decision resource name"
          />
          <span className="drs-page-id">#{selected.id}</span>
          {draftMeta?.isBase && (
            <span className="drs-chip drs-chip-base" title="This is the governed SynerGen reference — new client copies start from it. Edit deliberately.">
              SynerGen reference v{draftMeta.templateVersion ?? 1}
            </span>
          )}
          {!draftMeta?.isBase && draftMeta?.basedOn && (
            <span className="drs-chip" title={`Copied from resource #${draftMeta.basedOn.resourceId}${draftMeta.basedOn.copiedAt ? ` on ${new Date(draftMeta.basedOn.copiedAt).toLocaleDateString()}` : ""}. A copy — base updates do not flow in.`}>
              based on {draftMeta.basedOn.templateKey ?? `#${draftMeta.basedOn.resourceId}`}{draftMeta.basedOn.version ? ` v${draftMeta.basedOn.version}` : ""}
            </span>
          )}
          {draftProfile?.executable && !draftProfile.valid && (
            <span className="drs-page-invalid" title="The server rejects invalid machines on save — fix the issues listed in Pathway Specification.">
              ⚠ {draftProfile.issueCount} workflow issue{draftProfile.issueCount === 1 ? "" : "s"}
            </span>
          )}
          <span className="drs-page-status">
            {saving ? "Saving…" : dirty ? "Unsaved changes" : justSaved ? "✓ Saved" : ""}
          </span>
          {dirty && !saving && (
            <Button
              type="button" size="sm" variant="secondary"
              title="Throw away unsaved changes and reload the resource as it is on the server"
              onClick={() => {
                if (selected?.id && window.confirm("Discard unsaved changes and reload from the server?")) {
                  openResource(selected.id);
                }
              }}
            >
              Discard
            </Button>
          )}
          <Button type="button" size="sm" variant="primary" disabled={!dirty || saving} onClick={save}>
            {saving ? <Spinner size="sm" animation="border" style={{ width: 12, height: 12, borderWidth: 2 }} /> : "Save"}
          </Button>
        </div>
        {saveError && <Alert variant="danger" className="mt-2 mb-2" style={{ fontSize: 13 }}>{saveError}</Alert>}
        <div className="drs-page-body study-resource-form-drawer">
          <DecisionResourceStudio
            content={contentDraft}
            onContentChange={(c) => { setContentDraft(c); setDirty(true); }}
            isNew={isBlankConfig(contentDraft)}
            hasName={!!nameDraft && nameDraft !== "Untitled Decision Resource"}
            onSuggestName={(n) => { setNameDraft(n); setDirty(true); }}
            initialPortlet={targetPortlet?.id}
            sourceRegistry={sourceRegistry}
            clinicalMasters={clinicalMasters}
            hasRubric={!!selected.rubricId}
            showDemoTemplates={false}
          />
        </div>
      </div>
    );
  }

  // ── Landing: decision resources as governed artifacts ──
  const profileChips = (p: GovernanceProfile) => (
    <>
      {p.executable ? (
        <span className="drs-chip drs-chip-exec" title="Executable state machine — iLearn runs this against a server-owned instance">⚙ executable</span>
      ) : (
        <span className="drs-chip" title="Display-only workflow chain — upgrade in Pathway Specification to enforce it">display-only</span>
      )}
      {p.executable && !p.valid && (
        <span className="drs-chip drs-chip-warn">⚠ {p.issueCount} issue{p.issueCount === 1 ? "" : "s"}</span>
      )}
      <span className="drs-chip">{p.pathways} pathway{p.pathways === 1 ? "" : "s"}</span>
      <span className="drs-chip">{p.states} step{p.states === 1 ? "" : "s"}</span>
      {p.executable && (
        <>
          <span className="drs-chip">{p.hardStops} hard stop{p.hardStops === 1 ? "" : "s"}</span>
          <span className="drs-chip">{p.checkpoints} checkpoint{p.checkpoints === 1 ? "" : "s"}</span>
          <span className="drs-chip">{p.safetyExits} safety exit{p.safetyExits === 1 ? "" : "s"}</span>
        </>
      )}
    </>
  );

  return (
    <div className="drs-page">
      <div className="drs-page-hero">
        <div>
          <h1>{targetPortlet ? `${targetPortlet.icon} ${targetPortlet.label}` : "Decision Resource Studio"}</h1>
          <div className="drs-page-hero-sub">
            {targetPortlet
              ? `Pick a decision resource to open its ${targetPortlet.label} portlet`
              : conditionFilter
              ? `Condition: ${conditionFilter} — Configure · Govern · Publish`
              : "Configure · Govern · Publish — for clinical & pharmacy leaders"}
          </div>
        </div>
        {(!targetPortlet || targetPortlet.id === "pathways") && (
          <Button type="button" variant="primary" disabled={creating} onClick={createNew}>
            {creating ? "Creating…" : targetPortlet ? "＋ New pathway" : "＋ New decision resource"}
          </Button>
        )}
      </div>

      {listError && <Alert variant="danger" style={{ fontSize: 13 }}>{listError}</Alert>}

      {loading ? (
        <div className="drs-page-loading"><Spinner animation="border" /> Loading decision resources…</div>
      ) : resources.length === 0 ? (
        <div className="drs-upgrade-panel">
          <div className="drs-upgrade-title">No decision resources yet</div>
          <p>Create your first decision resource to start authoring pathways, guardrails, and monitoring rules.</p>
        </div>
      ) : (
        <>
          {baseTemplates.length > 0 && (
            <>
              <div className="drs-section-label">SynerGen reference resources <small>(out-of-the-box, governed — start a client copy from one)</small></div>
              <div className="drs-card-grid" style={{ marginBottom: 22 }}>
                {baseTemplates.map((r) => {
                  const p = profileOf(r.content);
                  const m = templateMetaOf(r.content);
                  return (
                    <div key={r.id} className="drs-resource-card drs-resource-card-base">
                      <div className="drs-resource-card-head">
                        <span className="drs-resource-card-name">{r.name}</span>
                        <span className="drs-page-id">#{r.id}</span>
                      </div>
                      {p.title && p.title !== r.name && <div className="drs-resource-card-title">{p.title}</div>}
                      <div className="drs-resource-card-chips">
                        <span className="drs-chip drs-chip-base" title={m.templateKey ? `Template key: ${m.templateKey}` : undefined}>SynerGen reference v{m.templateVersion ?? 1}</span>
                        {m.condition && <span className="drs-chip" title="Condition (Clinical Domains catalog)">🏥 {m.condition}</span>}
                        {profileChips(p)}
                      </div>
                      <div className="drs-base-actions">
                        <Button type="button" size="sm" variant="primary" disabled={creating} onClick={() => createFromBase(r)}>
                          {creating ? "Copying…" : "Start from this base"}
                        </Button>
                        <Button type="button" size="sm" variant="secondary" onClick={() => r.id && openResource(r.id)}>
                          Open base
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div className="drs-section-label">Your decision resources</div>
          {clientResources.length === 0 ? (
            <div className="drs-hint">None yet — start from a SynerGen reference above, or create a blank decision resource.</div>
          ) : (
            <div className="drs-card-grid">
              {clientResources.map((r) => {
                const p = profileOf(r.content);
                const m = templateMetaOf(r.content);
                return (
                  <button key={r.id} type="button" className="drs-resource-card" onClick={() => r.id && openResource(r.id)}>
                    <div className="drs-resource-card-head">
                      <span className="drs-resource-card-name">{r.name}</span>
                      <span className="drs-page-id">#{r.id}</span>
                    </div>
                    {p.title && p.title !== r.name && <div className="drs-resource-card-title">{p.title}</div>}
                    <div className="drs-resource-card-chips">
                      {m.basedOn && (
                        <span className="drs-chip" title={`Copied from resource #${m.basedOn.resourceId}${m.basedOn.copiedAt ? ` on ${new Date(m.basedOn.copiedAt).toLocaleDateString()}` : ""}`}>
                          from {m.basedOn.templateKey ?? `#${m.basedOn.resourceId}`}{m.basedOn.version ? ` v${m.basedOn.version}` : ""}
                        </span>
                      )}
                      {m.condition && <span className="drs-chip" title="Condition (Clinical Domains catalog)">🏥 {m.condition}</span>}
                      {profileChips(p)}
                    </div>
                    <div className="drs-resource-card-foot">
                      {r.lastModifiedDate ? `Modified ${new Date(r.lastModifiedDate).toLocaleDateString()}` : ""}
                      <span className="drs-resource-card-open">Open in Studio →</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DecisionResourceStudioPage;
